import { useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { DOCUMENT_TYPE, INSURANCE_TYPE } from '../../../utils/insuranceTypeUtils';
import { selectedSearchResultAtom } from '../../../store/insuranceSearchStore';
import useMasterData from './useMasterData';
import useSearchCondition from './useSearchCondition';
import useSearchResults from './useSearchResults';

/**
 * 搭載文書検索画面のメインカスタムフック
 *
 * 各サブフックを統合してオーケストレーションを行う。
 * このフックは以下の責務を持つ:
 * - マスターデータ・検索条件・検索結果の各サブフックを統合
 * - 検索実行・ページ変更・リセットのハンドラを提供
 * - 検索結果から既存詳細画面への遷移ロジックを集約
 *
 * @returns {Object} 搭載文書検索画面に必要な状態とハンドラ
 *
 * @returns {Array} returns.insuranceCompanies - 保険会社一覧（useMasterData）
 * @returns {boolean} returns.isMasterLoading - マスターデータ取得中フラグ
 * @returns {Error|null} returns.masterError - マスターデータ取得エラー
 *
 * @returns {number} returns.insuranceType - 生損保区分（useSearchCondition）
 * @returns {number[]} returns.selectedDocumentTypeIds - 選択中の文書種別IDリスト
 * @returns {number[]} returns.selectedCompanyIds - 選択中の保険会社IDリスト
 * @returns {string} returns.selectedProductType - 選択中の商品種類
 * @returns {string} returns.dateFrom - 加入時期開始（YYYY/MM/DD）
 * @returns {string} returns.dateTo - 加入時期終了（YYYY/MM/DD）
 * @returns {string} returns.keyword - 商品名キーワード
 * @returns {Function} returns.handleInsuranceTypeChange - 生損保区分変更ハンドラ
 * @returns {Function} returns.setSelectedDocumentTypeIds - 文書種別IDs設定関数
 * @returns {Function} returns.setSelectedCompanyIds - 保険会社IDs設定関数
 * @returns {Function} returns.setSelectedProductType - 商品種類設定関数
 * @returns {Function} returns.setDateFrom - 加入時期開始設定関数
 * @returns {Function} returns.setDateTo - 加入時期終了設定関数
 * @returns {Function} returns.setKeyword - キーワード設定関数
 *
 * @returns {Array} returns.items - 検索結果アイテム一覧（useSearchResults）
 * @returns {number} returns.total - 全件数
 * @returns {number} returns.page - 現在ページ番号
 * @returns {number} returns.totalPages - 総ページ数
 * @returns {boolean} returns.isLoading - 検索実行中フラグ
 * @returns {Error|null} returns.error - 検索エラー
 * @returns {boolean} returns.hasSearched - 検索実行済みフラグ
 *
 * @returns {Function} returns.handleSearch - 検索実行ハンドラ（1ページ目から検索）
 * @returns {Function} returns.handlePageChange - ページ変更ハンドラ
 * @returns {Function} returns.handleReset - 検索条件・結果全リセットハンドラ
 * @returns {Function} returns.handleNavigateToDetail - 詳細画面遷移ハンドラ
 */
const useDocumentCatalogSearch = () => {
  /**
   * サブフック: マスターデータ
   * 画面マウント時に保険会社一覧を取得する
   */
  const master = useMasterData();

  /**
   * サブフック: 検索条件
   * フォーム状態の管理とAPIリクエストボディの生成を担う
   */
  const condition = useSearchCondition();

  /**
   * サブフック: 検索結果
   * API呼び出しと結果・ページング・ローディング状態を管理する
   */
  const results = useSearchResults();

  const [, setSelectedSearchResult] = useAtom(selectedSearchResultAtom);

  /**
   * アクセス制限に基づいてフィルタリングされた保険会社一覧
   *
   * - 管理者: 全保険会社
   * - 代理店:
   *   - 指定なし(null) / policy_booklet: 全保険会社
   *   - その他(new_contract, maintenance, hikiuke, handbook): approved_document_types に該当コードを含む会社のみ
   */
  const filteredInsuranceCompanies = useMemo(() => {
    const { userInfo, insuranceCompanies, agencyInsuranceCompanies, documentTypesAccess } = master;
    // userInfo未取得（ロード中）の間は絞り込み判断ができないため全社表示にしておく。
    if (!userInfo || Object.keys(userInfo).length === 0) return insuranceCompanies;
    // 管理者はアクセス制限の対象外。全保険会社を表示する。
    if (userInfo.is_admin) return insuranceCompanies;
    // アクセス権情報未ロード時は全社表示にフォールバック（ローディング中・取得失敗時も表示を止めない）
    if (documentTypesAccess.length === 0) return insuranceCompanies;

    const { selectedDocumentTypeId } = condition;

    // 文書種別が未選択の間は絞り込み基準が無いため全社表示のままにする。
    if (!selectedDocumentTypeId) return insuranceCompanies;

    // 選択中の文書種別IDに対応するアクセス権情報が見つからない場合は絞り込まない（安全側のフォールバック）。
    const selectedType = documentTypesAccess.find(t => t.id === selectedDocumentTypeId);
    if (!selectedType) return insuranceCompanies;
    // 約款（POLICY_BOOKLET）は全社共通で閲覧可のため、アクセス権による絞り込み対象外。
    if (selectedType.document_type === DOCUMENT_TYPE.POLICY_BOOKLET) return insuranceCompanies;

    // 上記以外の文書種別は、代理店ごとの approved_document_types に含まれる会社のみに絞り込む。
    return agencyInsuranceCompanies.filter(c =>
      (c.approved_document_types || []).includes(selectedType.document_type)
    );
  }, [
    master.userInfo,
    master.insuranceCompanies,
    master.agencyInsuranceCompanies,
    master.documentTypesAccess,
    condition.selectedDocumentTypeId,
  ]);

  const { setSelectedCompanyIds } = condition;

  // 文書種別の変更に伴い filteredInsuranceCompanies が更新されたとき、
  // 新しい利用可能リストに存在しなくなった保険会社IDを選択状態から除去する。
  useEffect(() => {
    const validIds = new Set(filteredInsuranceCompanies.map(c => c.id));
    setSelectedCompanyIds(prev => {
      if (prev.length === 0) return prev;
      const filtered = prev.filter(id => validIds.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [filteredInsuranceCompanies, setSelectedCompanyIds]);

  /**
   * 検索を実行する（1ページ目から）
   * 検索ボタン押下時に呼び出される
   */
  const handleSearch = useCallback(async () => {
    const params = condition.buildSearchParams();
    await results.executeSearch(params, 1);
  }, [condition.buildSearchParams, results.executeSearch]);

  /**
   * 指定ページの検索結果を取得する
   * ページャーの前・次ボタン押下時に呼び出される
   *
   * @param {number} targetPage - 移動先のページ番号
   */
  const handlePageChange = useCallback(async (targetPage) => {
    const params = condition.buildSearchParams();
    await results.executeSearch(params, targetPage);
  }, [condition.buildSearchParams, results.executeSearch]);

  /**
   * 検索条件と検索結果を両方リセットする
   * リセットボタン押下時に呼び出される
   */
  const handleReset = useCallback(() => {
    condition.handleReset();
    results.resetResults();
  }, [condition.handleReset, results.resetResults]);

  /**
   * 検索結果の1件から対応する詳細画面に遷移する
   *
   * document_type_code によって遷移先を分岐する:
   * - DOCUMENT_TYPE.HIKIUKE → 引受目安詳細画面（/solicitor/insurance/hikiuke/detail）
   * - それ以外 → 文書検索結果詳細画面（/solicitor/insurance/system/detail）
   *
   * localStorage（selectedSearchResultAtom）に fromDocumentCatalog: true を付加することで、
   * 詳細画面側で Good/Bad フィードバックボタンの非表示とハイライトの無効化が行われる。
   *
   * @param {Object} item - 検索結果の DocumentCatalogItem
   */
  const handleNavigateToDetail = useCallback((item) => {
    if (item.document_type_code === DOCUMENT_TYPE.HIKIUKE) {
      // 引受目安詳細画面に遷移。
      // 引受目安固有の表示データ（目次/凡例/別表）を item.id をキーにして保存する
      // （通常の文書検索結果詳細画面とは異なるデータ構造・保存キーになる点に注意）。
      setSelectedSearchResult({
        [item.id]: {
          pdfUrl: item.file_name,
          companyName: item.insurance_company_name,
          diseaseName: item.document_name,
          pages: 1,
          highlights: null,
          queryId: null,
          fromDocumentCatalog: true,
          tableOfContents: item.table_of_contents ?? null,
          legends: item.legends ?? null,
          appendixTables: item.appendix_tables ?? null,
        },
      });
      window.open('/solicitor/insurance/hikiuke/detail', '_blank', 'noopener,noreferrer');
    } else {
      // 文書検索結果詳細画面に遷移。
      // summary/creationDateは搭載文書検索の一覧データに含まれないため、詳細画面側の初期値に委ねる。
      setSelectedSearchResult({
        pdfUrl: item.file_name,
        companyName: item.insurance_company_name,
        productName: item.document_name,
        creationDate: null,
        pages: 1,
        summary: '',
        queryId: null,
        insuranceType: item.insurance_type_id,
        indexName: item.index_name, // AIチャット用: 表示中文書の検索インデックス名
        classificationLabel: item.document_type_name || '', // AIチャット用: 文書種別（ドメインコンテキスト選択に使用）
        fromDocumentCatalog: true,
      });
      window.open('/solicitor/insurance/system/detail', '_blank', 'noopener,noreferrer');
    }
  }, [setSelectedSearchResult]);

  return {
    // マスターデータ
    ...master,
    // アクセス制限済み保険会社一覧（master.insuranceCompanies を上書き）
    insuranceCompanies: filteredInsuranceCompanies,
    // 検索条件
    ...condition,
    // 検索結果
    ...results,
    // オーケストレーターハンドラ
    handleSearch,
    handlePageChange,
    handleReset,
    handleNavigateToDetail,
  };
};

export default useDocumentCatalogSearch;
